// The Higher Self conversation.
//
// Two moving parts: /api/oracle streams the reply, and /api/chat/messages persists the transcript as
// ciphertext so the same conversation continues on the iPhone. The server resolves the persona's
// voice from a key (see the Worker's personas.ts), so the browser never ships prompt text — that's
// how the voice can be tuned without releasing a new client.

import { apiGet, apiPost, streamSSE } from './client';
import { openWithKey } from '../crypto/crypto-vault';

export type PersonaKey = 'higher_self' | 'buddy' | 'therapist' | 'advisor' | 'philosopher';

export const PERSONAS: { key: PersonaKey; label: string; blurb: string }[] = [
  { key: 'higher_self', label: 'Higher Self', blurb: 'The wisest version of you' },
  { key: 'buddy', label: 'The Buddy', blurb: 'Your ride-or-die friend' },
  { key: 'therapist', label: 'The Therapist', blurb: 'Goes a layer deeper' },
  { key: 'advisor', label: 'The Advisor', blurb: 'Direct and practical' },
  { key: 'philosopher', label: 'The Philosopher', blurb: 'The wider vantage point' },
];

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: Date;
}

interface ChatMessageRow {
  id: string;
  conversation_id?: string;
  role?: string;
  source?: string;
  content_cipher?: string | null;
  created_at?: string | null;
}

/**
 * Parse a timestamp from D1, which mixes two formats.
 *
 * Rows written by `datetime('now')` come back as "2026-06-28 15:35:14" — UTC, but with no marker
 * saying so, and the language spec says a date-time with no offset is *local*. Passing that straight
 * to `new Date` therefore shifted every history timestamp by the reader's own offset, which in a
 * transcript reads as messages arriving hours before they were sent. Rows written by a client arrive
 * as proper ISO with a Z and are already unambiguous.
 */
function parseTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  const normalised = /\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value.trim())
    ? `${value.trim().replace(' ', 'T')}Z`
    : value;
  const date = new Date(normalised);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Where a conversation came from, read off its id.
 *
 * iOS namespaces the id by entry point: `voice-…` for a spoken session and `experience-…` for the
 * helper that talks a journal entry out of you in the composer. Everything else is a Higher Self
 * reflection. They all land in the same table, so without this the list mixes a two-line voice memo
 * in among real reflections with nothing to tell them apart. Labelled rather than hidden — they are
 * still conversations the person had, and quietly dropping them would look like missing history.
 */
export type ConversationKind = 'reflection' | 'voice' | 'entry';

function kindOf(id: string): ConversationKind {
  if (id.startsWith('voice-')) return 'voice';
  if (id.startsWith('experience-')) return 'entry';
  return 'reflection';
}

export interface Conversation {
  id: string;
  /** Derived from the opening message, since titles are never stored server-side. */
  title: string;
  kind: ConversationKind;
  messageCount: number;
  startedAt: Date;
  lastMessageAt: Date;
}

interface ConversationRow {
  conversation_id?: string;
  message_count?: number;
  started_at?: string | null;
  last_message_at?: string | null;
  opening_cipher?: string | null;
}

/**
 * Trim an opening message down to something that reads like a title in a list.
 *
 * Deliberately not a summary: people recognise their own words faster than a paraphrase, and the
 * first sentence of what they typed is usually why they opened the thread.
 */
function titleFrom(opening: string): string {
  const firstLine = opening.trim().split('\n')[0].trim();
  if (!firstLine) return 'Untitled conversation';

  // Cut at the first sentence end when there is one, so a long opening does not become a paragraph.
  const sentence = firstLine.match(/^(.{12,}?)[.!?](\s|$)/);
  const text = (sentence ? sentence[1] : firstLine).trim();

  if (text.length <= 68) return text;
  // Break on a word boundary rather than mid-word.
  return `${text.slice(0, 68).replace(/\s+\S*$/, '')}…`;
}

/**
 * Every conversation this account has, on any device, newest first.
 *
 * Titles are decrypted here because the server only ever holds the opening message as ciphertext.
 * A thread whose opening cannot be opened still appears — losing the name is a much smaller failure
 * than hiding a conversation that exists.
 */
export async function fetchConversations(
  dek: CryptoKey,
  signal?: AbortSignal,
): Promise<Conversation[]> {
  const res = await apiGet<{ conversations: ConversationRow[] }>('/api/chat/conversations', signal);

  return Promise.all(
    (res.conversations ?? []).map(async (row): Promise<Conversation> => {
      let title = 'Untitled conversation';
      if (row.opening_cipher) {
        try {
          title = titleFrom(await openWithKey(dek, row.opening_cipher));
        } catch {
          // Written under a key we no longer hold. Still listed, just unnamed.
        }
      }
      const last = parseTimestamp(row.last_message_at) ?? new Date();
      const id = row.conversation_id ?? '';
      return {
        id,
        title,
        kind: kindOf(id),
        messageCount: row.message_count ?? 0,
        startedAt: parseTimestamp(row.started_at) ?? last,
        lastMessageAt: last,
      };
    }),
  );
}

export async function fetchMessages(
  dek: CryptoKey,
  conversationId: string,
  signal?: AbortSignal,
): Promise<ChatMessage[]> {
  const res = await apiGet<{ messages: ChatMessageRow[] }>(
    `/api/chat/messages?conversation_id=${encodeURIComponent(conversationId)}`,
    signal,
  );

  const decrypted = await Promise.all(
    (res.messages ?? []).map(async (row): Promise<ChatMessage | null> => {
      if (!row.content_cipher) return null;
      let content: string;
      try {
        content = await openWithKey(dek, row.content_cipher);
      } catch {
        // A message we can't open would render as an empty bubble, which reads as data loss.
        return null;
      }
      return {
        id: row.id,
        // Only "user" means the person; everything else is Oreia. iOS writes its replies as "oreia"
        // and this client wrote "assistant", so checking for the assistant label instead turned every
        // reply written on the phone into a bubble attributed to the user — the transcript came back
        // reading as though they had said Oreia's words. Mirrors iOS's own `role == "user"` test.
        role: row.role === 'user' ? 'user' : 'assistant',
        content,
        createdAt: parseTimestamp(row.created_at) ?? new Date(),
      };
    }),
  );

  return decrypted.filter((m): m is ChatMessage => m !== null);
}

/**
 * Persist one turn. Sends plaintext: the Worker seals it with the server-wrapped copy of the DEK
 * before storing, and substantive user turns also feed the entity extraction that builds the
 * portrait — which it can only do on text it can read.
 */
export async function storeMessage(
  conversationId: string,
  role: 'user' | 'assistant',
  content: string,
): Promise<void> {
  await apiPost('/api/chat/messages', {
    conversation_id: conversationId,
    // Stored as "oreia", which is what iOS writes, so one shared conversation does not end up with
    // two different words for the same speaker. Reads accept either.
    role: role === 'assistant' ? 'oreia' : 'user',
    source: 'chat',
    content,
  });
}

export interface StreamReplyOptions {
  prompt: string;
  persona: PersonaKey;
  /** Recent turns, so the reply can reference what was already said. Capped server-side. */
  history: { role: string; content: string }[];
  purpose?: 'journal';
  draftContext?: string;
  journalHistory?: boolean;
  journalSourceId?: string;
  journalSourceConsent?: boolean;
  reviewYear?: number;
  guide?: string;
  companionStyle?: string;
  onToken(token: string): void;
  signal?: AbortSignal;
}

export async function streamReply(options: StreamReplyOptions): Promise<void> {
  await streamSSE(
    '/api/oracle',
    {
      prompt: options.prompt,
      persona: options.persona,
      journalSourceId:options.journalSourceId, journalSourceConsent:options.journalSourceConsent, journalHistory: options.journalHistory, reviewYear: options.reviewYear, purpose: options.purpose, draftContext: options.draftContext, guide: options.guide, companionStyle: options.companionStyle,
      history: options.history,
    },
    options.onToken,
    options.signal,
  );
}

export interface WeaveResult {
  title: string;
  body: string;
}

/**
 * Turn a conversation into a journal entry.
 *
 * The Worker writes the entry's prose from the transcript; it does not save anything. That split is
 * deliberate — the text comes back for the user to read and edit before it becomes an entry, and the
 * entry itself is then sealed client-side like any other, so a woven entry is no less private than a
 * typed one.
 */
export async function weaveConversation(
  messages: { role: string; content: string }[],
  mode: 'waking' | 'dream',
  signal?: AbortSignal,
): Promise<WeaveResult> {
  const res = await apiPost<{ title?: string; body?: string }>(
    '/api/weave',
    { messages, style: 'full', mode },
    signal,
  );
  return { title: (res.title || '').trim(), body: (res.body || '').trim() };
}
